import {provideHttpClient} from '@angular/common/http';
import {DEFAULT_CURRENCY_CODE, enableProdMode, inject, provideAppInitializer} from '@angular/core';
import {bootstrapApplication} from '@angular/platform-browser';
import {provideAnimations} from '@angular/platform-browser/animations';
import {provideRouter} from '@angular/router';
import {AuthService, ROUTES} from '@emma-labs/broker';
import {
  DatadogLoggerService,
  ENGLISH_TRANSLATIONS,
  FeatureFlagService,
  FRENCH_TRANSLATIONS,
  LaunchDarklyService,
  LocalFeatureFlagService,
  LocalLoggerService,
  LocalStorageService,
  LoggerService,
} from '@emma-labs/shared/angular';
import {ENVIRONMENT, EnvironmentBroker, isDev} from '@emma-labs/shared/typescript';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {provideNgxMask} from 'ngx-mask';
import {ConfirmationService, MessageService} from 'primeng/api';
import {DialogService} from 'primeng/dynamicdialog';
import {catchError, first, switchMap} from 'rxjs';
import {AppComponent} from './app/app.component';
import {englishTranslation} from './app/translation/english-translation';
import {frenchTranslation} from './app/translation/french-translation';
import {environment} from './environments/environment';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const environmentFactory = (): EnvironmentBroker => new EnvironmentBroker(environment);

if (environment.enableProdMode) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideHttpClient(),
    provideNgxMask(),
    provideAnimations(),
    provideRouter(ROUTES),
    {provide: DEFAULT_CURRENCY_CODE, useValue: 'CAD'},
    {provide: ENVIRONMENT, useFactory: environmentFactory},
    {provide: ENGLISH_TRANSLATIONS, useValue: englishTranslation},
    {provide: FRENCH_TRANSLATIONS, useValue: frenchTranslation},
    MessageService,
    ConfirmationService,
    DialogService,
    provideNgxMask(),
    {
      provide: FeatureFlagService,
      useFactory: (env: EnvironmentBroker, localStorage: LocalStorageService) => {
        return isDev(env.environment)
          ? new LocalFeatureFlagService()
          : new LaunchDarklyService(env, localStorage);
      },
      deps: [ENVIRONMENT, LocalStorageService],
    },
    {
      provide: LoggerService,
      useFactory: (env: EnvironmentBroker) => {
        return isDev(env.environment) ? new LocalLoggerService() : new DatadogLoggerService(env);
      },
      deps: [ENVIRONMENT],
    },
    provideAppInitializer(() => {
      const initializerFn = (
        (authService: AuthService, featureFlagService: FeatureFlagService) => () =>
          authService.currentAgentSessionOrAuthError.pipe(
            first(),
            switchMap((session) => featureFlagService.initialize(atob(session.agent.id))),
            catchError(() => featureFlagService.initialize()),
          )
      )(inject(AuthService), inject(FeatureFlagService));
      return initializerFn();
    }),
  ],
}).catch((err) => console.error(err));
